import React from "react";
import { Lead, EmailMessage } from "@/api/entities";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Mail, Clock, Building2, User, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

export default function EmailThreadsList() {
  const { data: leads = [] } = useQuery({
    queryKey: ['leads'],
    queryFn: () => Lead.list({}, { orderBy: 'updated_date', orderDirection: 'desc' }),
  });

  const { data: emails = [] } = useQuery({
    queryKey: ['all-emails'],
    queryFn: () => EmailMessage.list({}, { orderBy: 'date', orderDirection: 'desc' }),
  });

  // Agrupar emails por lead y obtener el último de cada uno
  const emailsByLead = emails.reduce((acc, email) => {
    if (!acc[email.lead_id]) {
      acc[email.lead_id] = [];
    }
    acc[email.lead_id].push(email);
    return acc;
  }, {});

  // Filtrar leads no cerrados (nueva estructura + legacy) que tienen emails
  const ESTADOS_CERRADOS = ['cerrado', 'cerrado_perdido', 'cerrado_ganado'];
  const activeLeadsWithEmails = leads.filter(lead =>
    !ESTADOS_CERRADOS.includes(lead.estado) && emailsByLead[lead.id]?.length > 0
  );

  // Ordenar: primero los pendientes de respuesta, luego por fecha del último email
  const sortedLeads = activeLeadsWithEmails.sort((a, b) => {
    // Pendientes primero
    if (a.pendiente_email && !b.pendiente_email) return -1;
    if (!a.pendiente_email && b.pendiente_email) return 1;
    
    // Luego por fecha del último email
    const lastEmailA = emailsByLead[a.id]?.[0];
    const lastEmailB = emailsByLead[b.id]?.[0];
    
    if (!lastEmailA) return 1;
    if (!lastEmailB) return -1;
    
    return new Date(lastEmailB.date) - new Date(lastEmailA.date);
  });

  if (sortedLeads.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500 dark:text-gray-400">
        <Mail className="w-16 h-16 mb-4 opacity-30" />
        <p>No hay conversaciones de email</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[calc(100vh-200px)]">
      <div className="space-y-2 pr-4">
        {sortedLeads.map(lead => {
          const leadEmails = emailsByLead[lead.id] || [];
          const lastEmail = leadEmails[0];
          const inboundCount = leadEmails.filter(e => e.direction === 'inbound').length;
          const outboundCount = leadEmails.filter(e => e.direction === 'outbound').length;

          return (
            <Link
              key={lead.id}
              to={`${createPageUrl('Lead')}?id=${lead.id}&tab=emails&expandEmail=${lastEmail?.id || 'true'}`}
            >
              <Card className={`px-3 py-2 hover:shadow-md transition-all cursor-pointer ${
                lead.pendiente_email 
                  ? 'border-l-4 border-l-red-500 bg-red-50 dark:bg-red-950/30' 
                  : 'dark:bg-gray-900 dark:border-gray-800'
              }`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0 flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-gray-900 dark:text-white truncate">
                          {lead.nombre}
                        </span>
                        {lead.empresa && (
                          <span className="text-xs text-gray-400 truncate hidden sm:inline">· {lead.empresa}</span>
                        )}
                        {lead.pendiente_email && (
                          <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0">
                            Pendiente
                          </Badge>
                        )}
                      </div>
                      {lastEmail && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                          {lastEmail.subject} — {lastEmail.body_text?.substring(0, 60)}...
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0 text-xs text-gray-400">
                    <span className="text-green-600 dark:text-green-400">↓{inboundCount}</span>
                    <span className="text-indigo-600 dark:text-indigo-400">↑{outboundCount}</span>
                    {lastEmail && (
                      <span className="hidden sm:inline">
                        {format(new Date(lastEmail.date), "dd MMM", { locale: es })}
                      </span>
                    )}
                    <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </Card>
            </Link>
          );
        })}
      </div>
    </ScrollArea>
  );
}